import { ApiError } from './api';

export interface AdminSettings {
  sponsorAdsEnabled: boolean;
  starsSubscriptionEnabled: boolean;
  starsMonthlyPrice: number;
}

export interface AdminPlayer {
  id: string;
  telegramUserId: string;
  username: string | null;
  firstName: string;
  lastName: string | null;
  languageCode: string | null;
  countryCode: string | null;
  accountStatus: string;
  createdAt: string;
  lastSeenAt: string;
  activeNow: boolean;
  botStarted: boolean;
}

export interface AdminOverview {
  totalPlayers: number;
  activeNow: number;
  active24h: number;
  new24h: number;
  new7d: number;
  botStarted: number;
  countries: Array<{ countryCode: string; players: number }>;
  newestPlayers: AdminPlayer[];
  settings: AdminSettings;
}

export interface AnnouncementRecord {
  id: string;
  title: string;
  body: string;
  imageUrl: string | null;
  ctaUrl: string | null;
  ctaLabel: string | null;
  isActive: boolean;
  createdAt: string;
}

const headers = (initData: string): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `tma ${initData}`,
  'Content-Type': 'application/json'
});

const parse = async <T>(response: Response, fallback: string): Promise<T> => {
  const body = await response.json().catch(() => ({})) as T & { error?: { message?: string; code?: string } };
  if (!response.ok) throw new ApiError(body.error?.message ?? fallback, body.error?.code);
  return body;
};

export const loadAdminOverview = async (initData: string): Promise<AdminOverview> =>
  parse<AdminOverview>(await fetch('/api/v1/admin/overview', { cache: 'no-store', headers: headers(initData) }), 'Could not load Admin.');

export const loadAdminPlayers = async (initData: string, query = ''): Promise<AdminPlayer[]> => {
  const body = await parse<{ players: AdminPlayer[] }>(await fetch(`/api/v1/admin/players?q=${encodeURIComponent(query)}`, { cache: 'no-store', headers: headers(initData) }), 'Could not load players.');
  return body.players;
};

export const saveAdminSettings = async (initData: string, settings: Partial<AdminSettings>): Promise<AdminSettings> => {
  const body = await parse<{ settings: AdminSettings }>(await fetch('/api/v1/admin/settings', { method: 'PATCH', headers: headers(initData), body: JSON.stringify(settings) }), 'Could not save settings.');
  return body.settings;
};

export const loadAdminAnnouncements = async (initData: string): Promise<AnnouncementRecord[]> => {
  const body = await parse<{ announcements: AnnouncementRecord[] }>(await fetch('/api/v1/admin/announcements', { cache: 'no-store', headers: headers(initData) }), 'Could not load announcements.');
  return body.announcements;
};

export const createAdminAnnouncement = async (initData: string, input: {
  title: string; body: string; imageUrl: string | null; ctaUrl: string | null; ctaLabel: string | null; isActive: boolean;
}): Promise<AnnouncementRecord> => {
  const result = await parse<{ announcement: AnnouncementRecord }>(await fetch('/api/v1/admin/announcements', { method: 'POST', headers: headers(initData), body: JSON.stringify(input) }), 'Could not create announcement.');
  return result.announcement;
};

export const toggleAdminAnnouncement = async (initData: string, id: string, isActive: boolean): Promise<void> => {
  await parse<{ ok: boolean }>(await fetch(`/api/v1/admin/announcements/${encodeURIComponent(id)}`, { method: 'PATCH', headers: headers(initData), body: JSON.stringify({ isActive }) }), 'Could not update announcement.');
};
